import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    pointerWithin,
    TouchSensor,
    useDroppable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import clsx from "clsx";
import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import {Offcanvas} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import Button from "../button/Button";
import Checkbox from "../checkbox/Checkbox";
import AggrTypes from "../../../queries/technogroup-result-details/AggrTypes";
import Input from "../input/Input";
import MultiSelect from "../select/MultiSelect";
import Select from "../select/Select";
import {
    findHeaderContainer,
    getAggregateTypes,
    getLeafKeys,
    normalizeAggregate,
    moveHeader,
    removeColumns,
} from "./asyncTableColumns";
import {MAX_GROUP_LEVELS} from "./asyncTableGrouping";
import AsyncTableViews from "./AsyncTableViews";
import "./_async-table-settings.scss";

// grupy i poziomy grupowania tylko w pionie; kolumna także w poziomie - przeciągnięcie w lewo
// wyjmuje ją z grupy na poziom grup
const restrictNonColumnsToVerticalAxis = (args) =>
    args.active?.data.current?.column
        ? args.transform
        : restrictToVerticalAxis(args);

const MODIFIERS = [restrictNonColumnsToVerticalAxis];

// cel "na koniec grupy" - nagłówek grupy albo strefa pustej grupy
const INTO_PREFIX = "into:";

// przesunięcie w lewo (px), od którego kolumna z grupy wychodzi na poziom grup
const TO_ROOT_OFFSET = 24;

const ROOT_CONTAINER = "root";

// edycja nazw i usuwanie grup kolumn - bez przekazywania przez każdy poziom listy
const ColumnGroupsContext = createContext({});

// czy kolumna przeciągana z grupy ma wyjść na poziom grup
const isToRoot = (active, delta) =>
    !!active?.data.current?.column &&
    active.data.current?.sortable?.containerId !== ROOT_CONTAINER &&
    delta.x < -TO_ROOT_OFFSET;

const GROUP_DROPZONE_ID = "async-table-settings-group-by";

// lista poziomów grupowania ma własny SortableContext; id z prefiksem, bo te same kolumny są też na liście kolumn
const GROUP_LEVELS_CONTAINER = "async-table-settings-group-levels";
const LEVEL_PREFIX = "level:";

/**
 * Kursor nad strefą grupowania wygrywa. W pozostałych przypadkach liczy się tylko rodzeństwo
 * przeciąganego wiersza (ten sam SortableContext), więc kolumna nie wyjdzie ze swojej grupy.
 */
function collisionDetection(args) {
    const {active, droppableContainers} = args;
    if (active.data.current?.column) {
        // strefa grupowania wierszy i "na koniec grupy" (nagłówek grupy, pusta grupa) - pod kursorem
        const targets = pointerWithin({
            ...args,
            droppableContainers: droppableContainers.filter(
                (container) =>
                    container.id === GROUP_DROPZONE_ID ||
                    String(container.id).startsWith(INTO_PREFIX)
            ),
        });
        if (targets.length > 0) return targets;
        // kolumna może trafić przed dowolną kolumnę - w swojej grupie, w innej albo na poziomie grup
        return closestCenter({
            ...args,
            droppableContainers: droppableContainers.filter(
                (container) => !!container.data.current?.column
            ),
        });
    }
    const containerId = active.data.current?.sortable?.containerId;
    const siblings = droppableContainers.filter(
        (container) =>
            container.data.current?.sortable?.containerId === containerId
    );
    // klawiatura nie ma kursora - zostaje środek przeciąganego elementu
    if (!args.pointerCoordinates) {
        return closestCenter({...args, droppableContainers: siblings});
    }
    return pointerInsertion(args, siblings);
}

/**
 * Miejsce wstawienia liczone od kursora. Pozycje wierszy (droppableRects) są z układu sprzed przesunięć,
 * a kursor jest w układzie, w którym przeciągany element już "wypadł" z listy. Dlatego liczymy środki
 * rodzeństwa tak, jakby elementu nie było (wiersze pod nim wyżej o jego wysokość), i sprawdzamy,
 * za iloma z nich jest kursor - tyle wynosi nowy indeks.
 */
export function pointerInsertion(
    {active, droppableRects, pointerCoordinates},
    siblings
) {
    const activeRect = droppableRects.get(active.id);
    if (!activeRect) return [];
    const ordered = siblings
        .map((container) => ({
            container,
            rect: droppableRects.get(container.id),
        }))
        .filter((item) => item.rect)
        .sort((a, b) => a.rect.top - b.rect.top);

    let index = 0;
    for (const {container, rect} of ordered) {
        if (container.id === active.id) continue;
        const shift = rect.top > activeRect.top ? activeRect.height : 0;
        const center = rect.top - shift + rect.height / 2;
        if (pointerCoordinates.y > center) index++;
    }

    const target = ordered[index]?.container;
    if (!target) return [];
    return [{id: target.id, data: {droppableContainer: target, value: 0}}];
}

const GROUP_ROWS_OPTIONS = [5, 10, 25, 50].map((value) => ({
    value,
    name: value.toString(),
}));

const AGGREGATE_LABELS = {
    [AggrTypes.SUM]: "grid.sum",
    [AggrTypes.AVERAGE]: "grid.avg",
    [AggrTypes.WEIGHTED_AVERAGE]: "weightedAverage",
    [AggrTypes.MIN]: "minValue",
    [AggrTypes.MAX]: "maxVal",
    [AggrTypes.COUNT_DISTINCT]: "countUniqueValues",
    [AggrTypes.MOST_COMMON]: "mostCommonValue",
};

function matchesSearch(header, search) {
    if (!search) return true;
    return String(header.name ?? "")
        .toLowerCase()
        .includes(search);
}

/**
 * Wiersz, który można przeciągać za uchwyt. Przy wyszukiwaniu przeciąganie jest wyłączone,
 * bo lista pokazuje tylko część kolumn.
 */
function useSortableRow(columnKey, disabled, data) {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
        isOver,
        active,
    } = useSortable({id: columnKey, disabled, data});

    const style = useMemo(
        () => ({
            transform: CSS.Translate.toString(transform),
            transition,
        }),
        [transform, transition]
    );

    return {
        setNodeRef,
        style,
        isDragging,
        isOver,
        active,
        handleProps: {
            ref: setActivatorNodeRef,
            ...attributes,
            ...listeners,
        },
    };
}

/**
 * Podgląd przeciąganego wiersza. Grupa pokazuje się razem z kolumnami, żeby było widać, co się przesuwa.
 */
function DragPreview({header, hidden, depth = 0}) {
    if (!header.children) {
        return (
            <div
                className="async-table-settings-drag-overlay-row"
                style={{"--depth": depth}}>
                <i className="fa-solid fa-grip-dots-vertical fa-fw" />
                <span>{header.name}</span>
            </div>
        );
    }
    const keys = getLeafKeys([header]);
    const visibleCount = keys.filter((key) => !hidden.has(key)).length;
    return (
        <>
            <div
                className="async-table-settings-drag-overlay-row group"
                style={{"--depth": depth}}>
                <i className="fa-solid fa-grip-dots-vertical fa-fw" />
                <i className="fa-solid fa-chevron-down fa-fw" />
                <span>{header.name}</span>
                <span className="async-table-settings-group-count">
                    {visibleCount}/{keys.length}
                </span>
            </div>
            {header.children.map((child) => (
                <DragPreview
                    key={child.columnKey}
                    header={child}
                    hidden={hidden}
                    depth={depth + 1}
                />
            ))}
        </>
    );
}

function GroupLevelItem({column, index, onRemove}) {
    const {t} = useTranslation();
    const data = useMemo(() => ({level: column}), [column]);
    const {setNodeRef, style, isDragging, handleProps} = useSortableRow(
        LEVEL_PREFIX + column.columnKey,
        false,
        data
    );
    const onRemoveClick = useCallback(
        () => onRemove(column.columnKey),
        [onRemove, column.columnKey]
    );
    return (
        <li
            ref={setNodeRef}
            style={style}
            className={clsx(
                "async-table-settings-level",
                `level-${index + 1}`,
                {
                    first: index === 0,
                    dragging: isDragging,
                }
            )}>
            <DragHandle handleProps={handleProps} label={column.name} />
            <span className="async-table-group-level">{index + 1}</span>
            <span className="async-table-settings-level-name">
                {column.name}
            </span>
            <button
                type="button"
                className="async-table-settings-level-remove"
                aria-label={t("removeGroupLevel", {level: index + 1})}
                onClick={onRemoveClick}>
                <i className="fa-solid fa-xmark" />
            </button>
        </li>
    );
}

// miejsce na kolejny poziom - przyjmuje kolumnę przeciągniętą z listy kolumn
function GroupLevelSlot({level}) {
    const {t} = useTranslation();
    const {setNodeRef, isOver, active} = useDroppable({id: GROUP_DROPZONE_ID});
    const draggingColumn = !!active?.data.current?.column;
    return (
        <li
            ref={setNodeRef}
            className={clsx("async-table-settings-level-slot", {
                active: draggingColumn,
                over: draggingColumn && isOver,
            })}>
            <span className="async-table-group-level">{level}</span>
            <span>
                {t(
                    level === MAX_GROUP_LEVELS
                        ? "dragColumnToAddLastLevel"
                        : "dragColumnToAddLevel",
                    {level}
                )}
            </span>
        </li>
    );
}

/**
 * Poziomy grupowania w kolejności: uchwyt zmienia kolejność, × usuwa poziom,
 * a na końcu miejsce na kolejny poziom (do MAX_GROUP_LEVELS).
 */
function GroupLevelsList({columns, onRemove}) {
    const items = useMemo(
        () => columns.map((column) => LEVEL_PREFIX + column.columnKey),
        [columns]
    );
    return (
        <SortableContext
            id={GROUP_LEVELS_CONTAINER}
            items={items}
            strategy={verticalListSortingStrategy}>
            <ol className="async-table-settings-levels">
                {columns.map((column, index) => (
                    <GroupLevelItem
                        key={column.columnKey}
                        column={column}
                        index={index}
                        onRemove={onRemove}
                    />
                ))}
                {columns.length < MAX_GROUP_LEVELS && (
                    <GroupLevelSlot level={columns.length + 1} />
                )}
            </ol>
        </SortableContext>
    );
}

function DragHandle({handleProps, label, disabled}) {
    const {t} = useTranslation();
    return (
        <button
            type="button"
            className="async-table-settings-handle"
            aria-label={t("moveColumn", {name: label})}
            disabled={disabled}
            {...(handleProps ?? {})}>
            <i className="fa-solid fa-grip-dots-vertical fa-fw" />
        </button>
    );
}

function AggregateSelect({header, aggregates, onAggregateChange}) {
    const {t} = useTranslation();

    const types = useMemo(() => getAggregateTypes(header), [header]);

    const options = useMemo(
        () =>
            types?.map((type) => ({
                value: type,
                name: t(AGGREGATE_LABELS[type] ?? type),
            })),
        [types, t]
    );

    // kilka agregatów naraz - w tabeli jeden pod drugim; pusta lista to brak agregatu
    const value = useMemo(
        () =>
            normalizeAggregate(
                aggregates[header.columnKey] ?? header.aggregate
            ),
        [aggregates, header.columnKey, header.aggregate]
    );

    const onChange = useCallback(
        (list) => onAggregateChange(header.columnKey, list),
        [onAggregateChange, header.columnKey]
    );

    const selectedCountLabel = useCallback(
        (count) => t("selected", {number: count}),
        [t]
    );

    if (!types)
        return <span className="async-table-settings-no-aggregate">—</span>;

    return (
        <div className="async-table-settings-aggregate">
            <MultiSelect
                options={options}
                value={value}
                onChange={onChange}
                clearButton={false}
                placeholder={t("none")}
                selectedCountLabel={selectedCountLabel}
            />
        </div>
    );
}

/**
 * Kolumna grupująca (jak w projekcie): na górze listy, w kolorze swojego poziomu, zawsze widoczna
 * (w drzewie grup), bez zmiany kolejności; zamiast agregatu "Liczba wierszy", bo tyle pokazuje wiersz grupy.
 */
function GroupingColumnItem({column, level}) {
    const {t} = useTranslation();
    return (
        <li
            className={`async-table-settings-column async-table-settings-grouping-column level-${level}`}
            title={t("groupingColumnAlwaysVisible")}>
            <div className="async-table-settings-cell">
                <DragHandle label={column.name} disabled />
                <span className="async-table-group-level">{level}</span>
                <Checkbox label={column.name} checked disabled />
            </div>
            <span className="async-table-settings-no-aggregate">
                {t("rowsCount")}
            </span>
        </li>
    );
}

function ColumnItem({
    header,
    hidden,
    search,
    isLastVisible,
    onToggle,
    aggregates,
    onAggregateChange,
}) {
    const visible = !hidden.has(header.columnKey);
    const sortableData = useMemo(() => ({column: header}), [header]);
    const {setNodeRef, style, isDragging, handleProps} = useSortableRow(
        header.columnKey,
        !!search,
        sortableData
    );
    const onVisibleChange = useCallback(
        (checked) => onToggle([header.columnKey], checked),
        [onToggle, header.columnKey]
    );
    return (
        <li
            ref={setNodeRef}
            style={style}
            className={clsx("async-table-settings-column", {
                dragging: isDragging,
            })}>
            <div className="async-table-settings-cell">
                <DragHandle
                    handleProps={handleProps}
                    label={header.name}
                    disabled={!!search}
                />
                <Checkbox
                    label={header.name}
                    checked={visible}
                    disabled={visible && isLastVisible([header.columnKey])}
                    onChange={onVisibleChange}
                />
            </div>
            <AggregateSelect
                header={header}
                aggregates={aggregates}
                onAggregateChange={onAggregateChange}
            />
        </li>
    );
}

/**
 * Menu grupy kolumn (⋯): tylko zmiana nazwy i usunięcie (kolumny zostają, przechodzą na poziom grup).
 */
function GroupMenu({group}) {
    const {t} = useTranslation();
    const {startRename, removeGroup} = useContext(ColumnGroupsContext);
    const [open, setOpen] = useState(false);

    // zamknięcie po wyjściu fokusu poza menu
    const onBlur = useCallback((e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
    }, []);

    const onToggleMenu = useCallback(() => setOpen((prev) => !prev), []);

    const onRename = useCallback(() => {
        setOpen(false);
        startRename(group.columnKey);
    }, [startRename, group.columnKey]);

    const onRemove = useCallback(() => {
        setOpen(false);
        removeGroup(group.columnKey);
    }, [removeGroup, group.columnKey]);

    return (
        <div className="async-table-settings-group-menu" onBlur={onBlur}>
            <button
                type="button"
                className="async-table-settings-group-menu-toggle"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t("columnGroupOptions", {name: group.name})}
                onClick={onToggleMenu}>
                <i className="fa-solid fa-ellipsis fa-fw" />
            </button>
            {open && (
                <div
                    role="menu"
                    className="async-table-settings-group-menu-list">
                    <Button
                        type="button"
                        buttonStyle="text"
                        text={t("renameColumnGroup")}
                        onClick={onRename}
                    />
                    <Button type="button" buttonStyle="text" onClick={onRemove}>
                        <span>{t("removeColumnGroup")}</span>
                        <small>{t("removeColumnGroupHint")}</small>
                    </Button>
                </div>
            )}
        </div>
    );
}

/**
 * Edycja nazwy grupy kolumn (nowej albo istniejącej) - samo pole: Enter i wyjście z pola zatwierdzają,
 * Esc anuluje.
 */
function GroupNameEditor({group}) {
    const {t} = useTranslation();
    const {finishRename, cancelRename} = useContext(ColumnGroupsContext);
    const [name, setName] = useState(group.customName ?? group.name ?? "");

    // po Enter/Esc pole znika - blur, który może przyjść potem, nie może zatwierdzić drugi raz
    const done = useRef(false);

    const onConfirm = useCallback(() => {
        if (done.current) return;
        done.current = true;
        finishRename(group.columnKey, name.trim());
    }, [finishRename, group.columnKey, name]);

    const onCancel = useCallback(() => {
        if (done.current) return;
        done.current = true;
        cancelRename(group.columnKey);
    }, [cancelRename, group.columnKey]);

    const onKeyDown = useCallback(
        (e) => {
            if (e.key === "Enter") onConfirm();
            if (e.key === "Escape") {
                // Esc anuluje edycję zamiast zamykać szufladę
                e.stopPropagation();
                onCancel();
            }
            return true;
        },
        [onConfirm, onCancel]
    );

    return (
        <div className="async-table-settings-group-name-editor">
            <Input
                type="text"
                className="m-0"
                value={name}
                autofocus
                placeholder={t("columnGroupName")}
                onChange={setName}
                onKeyDown={onKeyDown}
                onBlur={onConfirm}
            />
        </div>
    );
}

// strefa pustej grupy - kolumny przeciąga się tu jak na nagłówek grupy
function EmptyGroupZone({group}) {
    const {t} = useTranslation();
    const {setNodeRef, isOver, active} = useDroppable({
        id: INTO_PREFIX + group.columnKey,
    });
    return (
        <li
            ref={setNodeRef}
            className={clsx("async-table-settings-group-empty", {
                over: isOver && !!active?.data.current?.column,
            })}>
            {t("emptyColumnGroupHint")}
        </li>
    );
}

function GroupItem({
    group,
    depth,
    hidden,
    search,
    isLastVisible,
    onToggle,
    aggregates,
    onAggregateChange,
}) {
    const {t} = useTranslation();
    const {editingId} = useContext(ColumnGroupsContext);

    const [open, setOpen] = useState(true);

    // tylko grupy najwyższego poziomu - grupa ma jeden poziom, zagnieżdżone z definicji tabeli się nie przenoszą
    const topLevel = depth === 0;

    const sortableData = useMemo(() => ({group}), [group]);
    const {setNodeRef, style, isDragging, handleProps} = useSortableRow(
        group.columnKey,
        !!search || !topLevel,
        sortableData
    );

    // nagłówek grupy przyjmuje kolumnę "na koniec grupy"
    const {
        setNodeRef: setHeaderDropRef,
        isOver: overHeader,
        active: dropActive,
    } = useDroppable({
        id: INTO_PREFIX + group.columnKey,
        disabled: !topLevel || !!search,
    });
    const dropInto = overHeader && !!dropActive?.data.current?.column;

    const keys = useMemo(() => getLeafKeys([group]), [group]);

    const visibleCount = keys.filter((key) => !hidden.has(key)).length;
    const allVisible = visibleCount === keys.length;

    const groupMatches = matchesSearch(group, search);

    const children = useMemo(() => {
        if (groupMatches) return group.children;
        // zostawiamy tylko dzieci (lub podgrupy), które pasują do wyszukiwania
        const filterChildren = (items) =>
            items.reduce((result, item) => {
                if (item.children) {
                    if (matchesSearch(item, search)) result.push(item);
                    else {
                        const nested = filterChildren(item.children);
                        if (nested.length > 0)
                            result.push({...item, children: nested});
                    }
                } else if (matchesSearch(item, search)) {
                    result.push(item);
                }
                return result;
            }, []);
        return filterChildren(group.children);
    }, [group, groupMatches, search]);

    const onToggleOpen = useCallback(() => setOpen((prev) => !prev), []);

    const onGroupVisibleChange = useCallback(
        () => onToggle(keys, !allVisible),
        [onToggle, keys, allVisible]
    );

    const empty = group.children.length === 0;
    const editing = editingId === group.columnKey;

    // pusta grupa jest widoczna tylko bez wyszukiwania
    if (children.length === 0 && (!empty || search) && !editing) return null;

    // przy wyszukiwaniu zawsze rozwijamy grupe, zeby bylo widac trafienia
    const expanded = open || !!search;

    return (
        <li
            ref={setNodeRef}
            style={style}
            className={clsx("async-table-settings-group", {
                dragging: isDragging,
            })}>
            <div
                ref={setHeaderDropRef}
                className={clsx("async-table-settings-group-header", {
                    "drop-into": dropInto,
                })}>
                <div className="async-table-settings-cell">
                    <DragHandle
                        handleProps={handleProps}
                        label={group.name}
                        disabled={!!search || !topLevel}
                    />
                    <button
                        type="button"
                        className="async-table-settings-group-toggle"
                        aria-expanded={expanded}
                        aria-label={t(expanded ? "collapse" : "expand")}
                        onClick={onToggleOpen}>
                        <i
                            className={`fa-solid fa-chevron-${expanded ? "down" : "right"} fa-fw`}
                        />
                    </button>
                    {editing ? (
                        <GroupNameEditor group={group} />
                    ) : (
                        <Checkbox
                            label={group.name}
                            checked={allVisible}
                            indeterminate={visibleCount > 0 && !allVisible}
                            // nie pozwalamy ukryć grupy, jeśli zawiera ostatnie widoczne kolumny
                            disabled={allVisible && isLastVisible(keys)}
                            onChange={onGroupVisibleChange}
                        />
                    )}
                </div>
                <div className="async-table-settings-group-side">
                    <span className="async-table-settings-group-count">
                        {visibleCount}/{keys.length}
                    </span>
                    {topLevel && !editing && <GroupMenu group={group} />}
                </div>
            </div>
            {expanded && !empty && (
                <ColumnList
                    headers={children}
                    containerId={group.columnKey}
                    depth={depth + 1}
                    hidden={hidden}
                    search={search}
                    isLastVisible={isLastVisible}
                    onToggle={onToggle}
                    aggregates={aggregates}
                    onAggregateChange={onAggregateChange}
                />
            )}
            {expanded && empty && !search && (
                <ul
                    className="async-table-settings-list"
                    style={{"--depth": depth + 1}}>
                    <EmptyGroupZone group={group} />
                </ul>
            )}
        </li>
    );
}

/**
 * Lista rodzeństwa. Każdy poziom ma własny SortableContext (`containerId`), a collisionDetection
 * szuflady pozwala przestawiać tylko w jego obrębie - kolumna nie wyjdzie ze swojej grupy nagłówków.
 * Wszystkie poziomy dzielą kolumny siatki (subgrid), dzięki czemu komórki są wyrównane jak w tabeli.
 */
function ColumnList({
    headers,
    containerId = ROOT_CONTAINER,
    depth = 0,
    hidden,
    search,
    isLastVisible,
    onToggle,
    aggregates,
    onAggregateChange,
}) {
    const items = useMemo(
        () => headers.map((header) => header.columnKey),
        [headers]
    );

    return (
        <SortableContext
            id={containerId}
            items={items}
            strategy={verticalListSortingStrategy}>
            <ul
                className="async-table-settings-list"
                style={{"--depth": depth}}>
                {headers.map((header) =>
                    header.children ? (
                        <GroupItem
                            key={header.columnKey}
                            group={header}
                            depth={depth}
                            hidden={hidden}
                            search={search}
                            isLastVisible={isLastVisible}
                            onToggle={onToggle}
                            aggregates={aggregates}
                            onAggregateChange={onAggregateChange}
                        />
                    ) : matchesSearch(header, search) ? (
                        <ColumnItem
                            key={header.columnKey}
                            header={header}
                            hidden={hidden}
                            search={search}
                            isLastVisible={isLastVisible}
                            onToggle={onToggle}
                            aggregates={aggregates}
                            onAggregateChange={onAggregateChange}
                        />
                    ) : null
                )}
            </ul>
        </SortableContext>
    );
}

/**
 * Szuflada z ustawieniami tabeli - widoczność, kolejność i agregaty kolumn.
 * @param {*} param0
 * @returns
 */
export default function AsyncTableSettings({
    show,
    onHide,
    headers,
    hiddenKeys,
    setHiddenKeys,
    onMoveColumn,
    onAddColumnGroup,
    onRenameColumnGroup,
    onRemoveColumnGroup,
    aggregates,
    onAggregateChange,
    onRestoreDefault,
    groupColumns,
    onRemoveGroup,
    onGroupBy,
    onMoveGroupLevel,
    views,
    settings,
    groupRowsLimit = 10,
    onGroupRowsLimitChange,
}) {
    const {t} = useTranslation();

    const [search, setSearch] = useState("");

    // formularz widoku w szufladzie: {mode: "saveAs" | "rename"}
    const [viewForm, setViewForm] = useState(null);

    // "Zapisz widok" w stopce: własny aktywny widok - zapis, w pozostałych przypadkach "Zapisz jako…"
    const onSaveView = useCallback(() => {
        if (views?.active?.IsOwner) views.save();
        else setViewForm({mode: "saveAs"});
    }, [views]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // przeciagany wiersz jedzie pod kursorem jako podglad (DragOverlay), a na liscie zostaje miejsce zrodla;
    // dzieki temu nad strefa grupowania wiersz nie "wraca" na liste
    const [dragged, setDragged] = useState(null);

    // znacznik w podglądzie: dokąd trafi kolumna ("→ Grupa", "bez grupy")
    const [dropTag, setDropTag] = useState(null);

    const groupNames = useMemo(
        () =>
            Object.fromEntries(
                headers
                    .filter((header) => header.children)
                    .map((header) => [header.columnKey, header.name])
            ),
        [headers]
    );

    // robocza kopia listy kolumn podczas przeciągania kolumny między grupami - kolumna od razu "wchodzi"
    // do listy docelowej, więc robi się w niej miejsce jak przy zwykłym przestawianiu
    const [draft, setDraft] = useState(null);
    const draftRef = useRef(null);
    draftRef.current = draft;

    // lista, z której wyszła przeciągana kolumna
    const originContainer = useRef(null);

    // bieżąca lista kolumn (ustawiana niżej, po wyliczeniu) - dla callbacków przeciągania
    const listHeadersRef = useRef([]);

    const onDragStart = useCallback(({active}) => {
        const {column, group, level} = active.data.current ?? {};
        setDragged(column ?? group ?? level ?? null);
        setDropTag(null);
        originContainer.current = active.data.current?.sortable?.containerId;
    }, []);

    const onDragOver = useCallback(({active, over, delta}) => {
        if (!active.data.current?.column || !over) return;
        const overId = String(over.id);
        let container;
        let before = null;
        const into = overId.startsWith(INTO_PREFIX);
        if (into) {
            container = overId.slice(INTO_PREFIX.length);
        } else if (over.data.current?.column) {
            container = over.data.current.sortable.containerId;
            before = over.id;
        } else {
            return;
        }
        // odsunięta w lewo kolumna stoi na poziomie grup - kolumny w grupach nie wciągają jej z powrotem
        if (container !== ROOT_CONTAINER && delta.x < -TO_ROOT_OFFSET) return;
        setDraft((prev) => {
            const tree = prev ?? listHeadersRef.current;
            const current = findHeaderContainer(tree, active.id);
            if (!current) return prev;
            if (current.containerKey === container) {
                // nagłówek własnej grupy też znaczy "na koniec grupy"; resztę w tej samej liście przesuwa sortable
                const isLast = current.index === current.list.length - 1;
                return into && !isLast
                    ? moveHeader(tree, active.id, container, null)
                    : prev;
            }
            return moveHeader(tree, active.id, container, before);
        });
    }, []);

    const onDragMove = useCallback(
        ({active, over, delta}) => {
            // przeciągnięcie w lewo: kolumna w kopii przechodzi na poziom grup, zaraz za swoją grupę -
            // miejsce upuszczenia ma pełną szerokość i stoi tam, gdzie kolumna trafi
            if (isToRoot(active, delta)) {
                setDraft((prev) => {
                    const tree = prev ?? listHeadersRef.current;
                    const groupIndex = tree.findIndex(
                        (header) =>
                            header.children &&
                            getLeafKeys([header]).includes(active.id)
                    );
                    if (groupIndex === -1) return prev;
                    return moveHeader(
                        tree,
                        active.id,
                        ROOT_CONTAINER,
                        tree[groupIndex + 1]?.columnKey ?? null
                    );
                });
            }
            if (!active.data.current?.column || !over) return setDropTag(null);
            const overId = String(over.id);
            if (overId.startsWith(INTO_PREFIX)) {
                const name = groupNames[overId.slice(INTO_PREFIX.length)];
                return setDropTag(t("toColumnGroup", {name}));
            }
            if (isToRoot(active, delta)) return setDropTag(t("withoutGroup"));
            const to = active.data.current?.sortable?.containerId;
            if (to === originContainer.current) return setDropTag(null);
            setDropTag(
                to === ROOT_CONTAINER
                    ? t("withoutGroup")
                    : t("toColumnGroup", {name: groupNames[to]})
            );
        },
        [groupNames, t]
    );

    const onDragCancel = useCallback(() => {
        setDragged(null);
        setDropTag(null);
        setDraft(null);
    }, []);

    /**
     * Kolumna skończyła w innej liście niż zaczęła (robocza kopia) - zapis w układzie względem sąsiadów:
     * przed następnym elementem, za poprzednim albo na koniec pustej grupy.
     * @returns {boolean} czy przeniesienie zostało obsłużone
     */
    const commitCrossListMove = useCallback(
        (active, over) => {
            const tree = draftRef.current;
            if (!tree || !active.data.current?.column) return false;
            const current = findHeaderContainer(tree, active.id);
            if (!current || current.containerKey === originContainer.current) {
                return false;
            }
            let list = current.list;
            let index = current.index;
            // ostatnie przestawienie już w liście docelowej (robi je sortable, nie kopia)
            if (
                over?.data.current?.column &&
                over.data.current.sortable?.containerId ===
                    current.containerKey &&
                over.id !== active.id
            ) {
                const to = list.findIndex(
                    (header) => header.columnKey === over.id
                );
                list = list.slice();
                list.splice(to, 0, list.splice(index, 1)[0]);
                index = to;
            }
            const next = list[index + 1];
            const prev = list[index - 1];
            if (next) onMoveColumn(active.id, next.columnKey);
            else if (prev)
                onMoveColumn(active.id, prev.columnKey, {after: true});
            else onMoveColumn(active.id, current.containerKey, {into: true});
            return true;
        },
        [onMoveColumn]
    );

    const onDragEnd = useCallback(
        ({active, over, delta}) => {
            setDragged(null);
            setDropTag(null);
            const crossList =
                over?.id !== GROUP_DROPZONE_ID &&
                commitCrossListMove(active, over);
            setDraft(null);
            if (!over || crossList) return;
            const overId = String(over.id);
            if (overId.startsWith(INTO_PREFIX)) {
                onMoveColumn(active.id, overId.slice(INTO_PREFIX.length), {
                    into: true,
                });
                return;
            }
            // kolumna przeciągnięta w lewo wychodzi z grupy (także upuszczona na swoje miejsce)
            if (isToRoot(active, delta)) {
                onMoveColumn(active.id, over.id, {toRoot: true});
                return;
            }
            if (active.id === over.id) return;
            if (over.id === GROUP_DROPZONE_ID) {
                if (active.data.current?.column) onGroupBy(active.id);
                return;
            }
            if (active.data.current?.level) {
                onMoveGroupLevel(
                    String(active.id).slice(LEVEL_PREFIX.length),
                    String(over.id).slice(LEVEL_PREFIX.length)
                );
                return;
            }
            onMoveColumn(active.id, over.id);
        },
        [onMoveColumn, onGroupBy, onMoveGroupLevel, commitCrossListMove]
    );

    const allKeys = useMemo(() => getLeafKeys(headers), [headers]);

    const hidden = useMemo(() => new Set(hiddenKeys), [hiddenKeys]);

    const visibleCount = allKeys.filter((key) => !hidden.has(key)).length;

    // czy ukrycie podanych kolumn zostawiłoby tabelę bez żadnej widocznej kolumny
    const isLastVisible = useCallback(
        (keys) => {
            const toHide = new Set(keys);
            return allKeys.every((key) => hidden.has(key) || toHide.has(key));
        },
        [allKeys, hidden]
    );

    const onToggle = useCallback(
        (keys, visible) => {
            setHiddenKeys((prev) => {
                const next = new Set(prev);
                keys.forEach((key) =>
                    visible ? next.delete(key) : next.add(key)
                );
                return [...next];
            });
        },
        [setHiddenKeys]
    );

    const onSearchKeyDown = useCallback(
        (e) => {
            // Esc czysci wyszukiwanie zamiast zamykac szuflade
            if (e.key === "Escape" && search) {
                e.stopPropagation();
                setSearch("");
            }
            return true;
        },
        [search]
    );

    const normalizedSearch = search.trim().toLowerCase();

    // grupa kolumn w edycji nazwy; `isNew` - anulowanie usuwa świeżo dodaną grupę
    const [editing, setEditing] = useState(null);

    const onNewColumnGroup = useCallback(() => {
        const id = onAddColumnGroup("");
        setEditing({id, isNew: true});
    }, [onAddColumnGroup]);

    const columnGroupsContext = useMemo(
        () => ({
            editingId: editing?.id,
            startRename: (id) => setEditing({id, isNew: false}),
            finishRename: (id, name) => {
                // pusta nazwa: nowa grupa dostaje domyślną, istniejąca zostaje bez zmian
                if (name || editing?.isNew) {
                    onRenameColumnGroup(id, name || t("newColumnGroup"));
                }
                setEditing(null);
            },
            cancelRename: (id) => {
                if (editing?.isNew) onRemoveColumnGroup(id);
                setEditing(null);
            },
            removeGroup: onRemoveColumnGroup,
        }),
        [editing, onRenameColumnGroup, onRemoveColumnGroup, t]
    );

    // kolumny grupujące są na górze listy, więc znikają ze swoich miejsc (i z grup nagłówków)
    const listHeaders = useMemo(
        () =>
            removeColumns(
                headers,
                (groupColumns ?? []).map((column) => column.columnKey)
            ),
        [headers, groupColumns]
    );

    listHeadersRef.current = listHeaders;

    const groupingColumns = useMemo(
        () =>
            (groupColumns ?? []).filter((column) =>
                matchesSearch(column, normalizedSearch)
            ),
        [groupColumns, normalizedSearch]
    );

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement="end"
            className="async-table-settings"
            backdropClassName="async-table-settings-backdrop">
            <div className="async-table-settings-header">
                <h4>{t("tableSettings")}</h4>
                <button
                    type="button"
                    className="async-table-settings-close"
                    aria-label={t("close")}
                    onClick={onHide}>
                    <i className="fa-solid fa-xmark" />
                </button>
            </div>
            <DndContext
                sensors={sensors}
                collisionDetection={collisionDetection}
                modifiers={MODIFIERS}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDragMove={onDragMove}
                onDragEnd={onDragEnd}
                onDragCancel={onDragCancel}>
                <Offcanvas.Body>
                    {views?.enabled && (
                        <AsyncTableViews
                            views={views}
                            settings={settings}
                            form={viewForm}
                            setForm={setViewForm}
                            onRestoreDefault={onRestoreDefault}
                        />
                    )}
                    <section className="async-table-settings-section">
                        <div className="async-table-settings-section-header">
                            <h6>{t("grouping")}</h6>
                        </div>
                        <GroupLevelsList
                            columns={groupColumns ?? []}
                            onRemove={onRemoveGroup}
                        />
                        <div className="async-table-settings-group-rows">
                            <span>{t("rowsInExpandedGroup")}</span>
                            <div className="async-table-settings-group-rows-select">
                                <Select
                                    options={GROUP_ROWS_OPTIONS}
                                    value={groupRowsLimit}
                                    onChange={onGroupRowsLimitChange}
                                    clearButton={false}
                                />
                            </div>
                        </div>
                    </section>
                    <div className="async-table-settings-section-header">
                        <h6>{t("grid.columns")}</h6>
                        <span>
                            {t("visibleColumnsCount", {
                                visible: visibleCount,
                                all: allKeys.length,
                            })}
                        </span>
                    </div>
                    <div className="async-table-settings-search">
                        <i className="fa-solid fa-magnifying-glass" />
                        <Input
                            type="search"
                            className="m-0"
                            autocomplete="off"
                            value={search}
                            placeholder={t("searchColumn")}
                            onChange={setSearch}
                            onKeyDown={onSearchKeyDown}
                        />
                    </div>
                    <div className="async-table-settings-columns">
                        <div
                            className="async-table-settings-list-header"
                            aria-hidden="true">
                            <span>{t("filterGrid.column")}</span>
                            <span>{t("aggregation")}</span>
                        </div>
                        {groupingColumns.length > 0 && (
                            <ul className="async-table-settings-list async-table-settings-grouping-list">
                                {groupingColumns.map((column) => (
                                    <GroupingColumnItem
                                        key={column.columnKey}
                                        column={column}
                                        level={groupColumns.indexOf(column) + 1}
                                    />
                                ))}
                            </ul>
                        )}
                        <ColumnGroupsContext.Provider
                            value={columnGroupsContext}>
                            <ColumnList
                                headers={draft ?? listHeaders}
                                hidden={hidden}
                                search={normalizedSearch}
                                isLastVisible={isLastVisible}
                                onToggle={onToggle}
                                aggregates={aggregates}
                                onAggregateChange={onAggregateChange}
                            />
                        </ColumnGroupsContext.Provider>
                    </div>
                    <Button
                        type="button"
                        className="async-table-settings-new-group"
                        icon={<i className="fa-solid fa-plus" />}
                        text={t("newColumnGroup")}
                        disabled={!!editing}
                        onClick={onNewColumnGroup}
                    />
                </Offcanvas.Body>
                <DragOverlay dropAnimation={null}>
                    {dragged && (
                        <div className="async-table-settings-drag-overlay">
                            <DragPreview header={dragged} hidden={hidden} />
                            {dropTag && (
                                <span className="async-table-settings-drop-tag">
                                    {dropTag}
                                </span>
                            )}
                        </div>
                    )}
                </DragOverlay>
            </DndContext>
            {views?.enabled ? (
                // jak w projekcie: informacja o niezapisanych zmianach, "Zamknij" i "Zapisz widok"
                <div className="async-table-settings-footer">
                    <small className="async-table-settings-footer-hint">
                        {views.changed ? t("tableViewUnsavedChanges") : ""}
                    </small>
                    <Button
                        type="button"
                        text={t("close")}
                        onClick={onHide}
                        className="m-0"
                    />
                    <Button
                        type="button"
                        buttonColor="success"
                        text={t("saveTableView")}
                        disabled={!views.changed}
                        onClick={onSaveView}
                        className="m-0"
                    />
                </div>
            ) : (
                <div className="async-table-settings-footer">
                    <Button
                        type="button"
                        buttonStyle="text"
                        icon={<i className="fa-solid fa-rotate-left" />}
                        text={t("restoreDefault")}
                        onClick={onRestoreDefault}
                        className="m-0"
                    />
                    <Button
                        type="button"
                        text={t("close")}
                        onClick={onHide}
                        className="m-0"
                    />
                </div>
            )}
        </Offcanvas>
    );
}
